import * as React from "react";
import {useCallback, useMemo} from "react";
import {Box} from "@mui/material";
import {FlexList} from "../../../../components/flexlist/FlexList";
import {FlexListColumn} from "../../../../components/flexlist/FlexTypes";
import {ItemIssue, SortFindManyItemIssueInput, useItemIssuesLazyQuery} from "../../../../Queries";
import {Thumbnail} from "../../../../components/Thumbnail";
import {BrandItemPagePath} from "../BrandItemPage";
import {LinkTo} from "../../../../components/LinkTo";

type ItemIssuesListProps = {
   brandItemId?: string;
   brandHouseId?: string;
   hideHeader?: boolean
};

const PAGE_SIZE = 20;

export const ItemIssuesList = ({
                                  brandItemId,
                                  brandHouseId,
                                  hideHeader
                               }: ItemIssuesListProps) => {
   const [fetchMore] = useItemIssuesLazyQuery({
      fetchPolicy: "no-cache"
   });

   const columns: FlexListColumn<ItemIssue>[] = useMemo(() => [
      {
         id: 'image',
         label: 'Source Image',
         field: 'imageUrl',
         render: (url) => {
            if (url) {
               return <Thumbnail image={url} size={70}/>;
            }
            return '-';
         },
         width: 10
      }, {
         id: 'processedImage',
         label: 'Processed Image',
         field: 'processedUrl',
         render: (url) => {
            if (url) {
               return <Thumbnail image={url} size={70}/>;
            }
            return '-';
         },
         width: 10
      },
      {
         id: 'brandItem',
         label: 'Item',
         field: 'brandItemId',
         width: 15,
         render: (itemId, row) => {
            const name = row?.brandItem?.nameLocalized?.[0]?.value || 'Item';
            return <LinkTo to={BrandItemPagePath(row?.brandHouseId, row?.brandItemId, true)}>{name}</LinkTo>
         }
      }, {
         id: 'brandHouse',
         label: 'Brand',
         field: 'brandItem.brandHouse.name',
         width: 15,
         render: (brandName, row) => {
            return brandName || row?.brandItem?.brandHouse?.name || '-';
         }
      },
      {
         id: 'location',
         label: 'Location',
         field: 'location',
         width: 10
      },
      {
         id: 'issue',
         label: 'Issue',
         field: 'issue',
         width: 15
      },
      {
         id: 'createdAt',
         label: 'Created',
         field: 'createdAt',
         width: 10,
         render: (date) => {
            return new Date(date).toLocaleDateString(navigator.language || 'da-dk', {
               month: 'short',
               year: 'numeric',
               day: 'numeric'
            });
         }
      }
      //eslint-disable-next-line
   ], []);

   const onRequestData = useCallback(async (search: string | undefined, sort: string | undefined, page: number, filter?: any, maxItems?: number) => {
      const {data} = await fetchMore({
         variables: {
            page: page,
            sort: (sort as SortFindManyItemIssueInput) || SortFindManyItemIssueInput._ID_DESC,
            perPage: maxItems || PAGE_SIZE,
            search: search,
            filter: {
               brandHouseId: brandHouseId,
               brandItemId: brandItemId,
               ...filter
            }
         }
      });

      const items = (data?.ItemIssues?.items || []) as ItemIssue[];
      return {
         data: items,
         totalCount: data?.ItemIssues?.count || 0
      }
   }, [fetchMore, brandHouseId, brandItemId]);

   return (
      <Box>
         <FlexList
            globalId="itemIssues"
            itemsPerPage={PAGE_SIZE}
            columns={columns}
            onRequestData={onRequestData}
            options={{
               searchLabel: "Search issues",
               hideHeader: hideHeader || false
            }}
         />
      </Box>
   );
};